/**
 * FileCard
 *
 * Grid view card for a single file. Shows thumbnail, filename, size, and badges.
 */

import React from 'react';
import { FileText, Image, Film, Music, Archive, File as FileIcon, Download, Trash2, Lock, Shield, Key } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { FileRecord } from '@/services/api/fileService';

interface FileCardProps {
  file: FileRecord;
  onSelect?: (file: FileRecord) => void;
  onDownload?: (file: FileRecord) => void;
  onDelete?: (file: FileRecord) => void;
  showFileSize?: boolean;
}

const getFileIcon = (contentType: string) => {
  if (contentType.startsWith('image/')) return Image;
  if (contentType.startsWith('video/')) return Film;
  if (contentType.startsWith('audio/')) return Music;
  if (contentType.includes('zip') || contentType.includes('tar') || contentType.includes('rar')) return Archive;
  if (contentType.includes('pdf') || contentType.includes('text') || contentType.includes('document')) return FileText;
  return FileIcon;
};

const formatSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const EncryptionIcon: React.FC<{ type: string }> = ({ type }) => {
  switch (type) {
    case 'org': return <Shield className="w-3 h-3 text-blue-500" />;
    case 'user_protected': return <Lock className="w-3 h-3 text-amber-500" />;
    case 'per_file': return <Key className="w-3 h-3 text-purple-500" />;
    default: return null;
  }
};

export const FileCard: React.FC<FileCardProps> = ({ file, onSelect, onDownload, onDelete, showFileSize = true }) => {
  const Icon = getFileIcon(file.contentType);
  const isImage = file.contentType.startsWith('image/');

  return (
    <div
      className="group border rounded-lg p-3 hover:bg-muted/50 cursor-pointer transition-colors"
      onClick={() => onSelect?.(file)}
    >
      {/* Thumbnail / Icon */}
      <div className="aspect-square bg-muted rounded flex items-center justify-center mb-2 overflow-hidden">
        {isImage && file.thumbnails?.medium ? (
          <img src={file.thumbnails.medium} alt={file.filename} className="w-full h-full object-cover" />
        ) : (
          <Icon className="w-10 h-10 text-muted-foreground" />
        )}
      </div>

      {/* File Info */}
      <p className="text-sm font-medium truncate" title={file.filename}>{file.filename}</p>
      <div className="flex items-center gap-1 mt-1">
        {showFileSize !== false && <span className="text-xs text-muted-foreground">{formatSize(file.size)}</span>}
        <EncryptionIcon type={file.encryptionType} />
        {file.visibility === 'public' && <Badge variant="outline" className="text-[10px] h-4 px-1">Public</Badge>}
      </div>

      {/* Actions (show on hover) */}
      <div className="flex gap-1 mt-2 opacity-0 group-hover:opacity-100 transition-opacity">
        {onDownload && (
          <Button size="sm" variant="ghost" className="h-6 text-xs" onClick={(e) => { e.stopPropagation(); onDownload(file); }}>
            <Download className="w-3 h-3 mr-1" /> Download
          </Button>
        )}
        {onDelete && (
          <Button size="sm" variant="ghost" className="h-6 text-xs text-destructive" onClick={(e) => { e.stopPropagation(); onDelete(file); }}>
            <Trash2 className="w-3 h-3" />
          </Button>
        )}
      </div>
    </div>
  );
};
